import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import FormControl from "react-bootstrap/FormControl";
import Container from "react-bootstrap/Container";
import InputGroup from "react-bootstrap/InputGroup";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Stack from "react-bootstrap/Stack";
import Spinner from "react-bootstrap/Spinner";
import Dropdown from "react-bootstrap/Dropdown";
import Badge from "react-bootstrap/Badge";
import {
  CC2MasterSheetDataRow,
  RetrievePalletLabelRowResult,
} from "../../workers/sheets";
import { useContext, useEffect, useState } from "react";
import { createWorkerFactory, useWorker } from "@shopify/react-web-worker";
import { UserAuthenticatedContext } from "../profile";
import { Printer } from "react-bootstrap-icons";
import { useCircleDarkMode } from "../../hooks/useCircleDarkMode";

const createSheetsWorker = createWorkerFactory(
  () => import("../../workers/sheets")
);

export interface PalletLabelPrintProps {
  /**
   * Spreadsheet ID for the PM sheet where pallet labels are printed.
   * If not provided, the user will select it.
   */
  pmSpreadsheetId?: string;
  activePMID: string;

  palletTypeInput?: "A" | "B" | "R"; // Optional. If not provided, user will select it
  palletNumberInput?: string; // Optional. If not provided, user will enter it

  modalSize?: "sm" | "lg" | "xl";

  closePalletLabelPrintModal?: () => void;

  currentRowPalletInfo?: {
    clientId: string;
    comments: string;
    catalogInfo: {
      clientItemId: string;
      name: string;
    };
    legacyPMID: string;
  };

  cc2Data: CC2MasterSheetDataRow[];
}

export type UniquePMEntry = {
  pmSpreadsheetId: string;
  clients: string[];
  otherData: Omit<CC2MasterSheetDataRow, "client" | "pmSpreadsheetId">[];
};

export const PalletLabelPrint = ({
  pmSpreadsheetId: incomingPMSpreadsheetId,
  activePMID,
  palletTypeInput,
  palletNumberInput,
  currentRowPalletInfo,
  closePalletLabelPrintModal,
  cc2Data,
}: PalletLabelPrintProps) => {
  const sheetsWorker = useWorker(createSheetsWorker);
  const userAuthenticatedContext = useContext(UserAuthenticatedContext);

  const darkMode = useCircleDarkMode();

  const [palletLabelErrors, setPalletLabelErrors] = useState<string[]>([]);
  const [palletRetrieveResults, setPalletRetrieveResults] = useState<
    RetrievePalletLabelRowResult[]
  >([]);

  const [internalError, setInternalError] = useState<string>();

  const [selectedPalletType, setSelectedPalletType] = useState(
    palletTypeInput ?? undefined
  );
  const [palletNumber, setPalletNumber] = useState(palletNumberInput);

  const [processingPalletLabel, setProcessingPalletLabel] = useState(false);
  const [showPalletLabelErrorsModal, setShowPalletLabelErrorsModal] =
    useState(false);

  const [willPrintCurrentRow, setWillPrintCurrentRow] = useState(false);
  useEffect(() => {
    setWillPrintCurrentRow(palletNumber === palletNumberInput);
  }, [palletNumber, palletNumberInput]);

  const showPalletNumberCurrentRowComparison =
    incomingPMSpreadsheetId && activePMID;

  const uniquePMs = cc2Data.reduce((acc, curr) => {
    const { pmSpreadsheetId, client, ...otherData } = curr;
    if (!acc[pmSpreadsheetId]) {
      acc[pmSpreadsheetId] = {
        pmSpreadsheetId,
        clients: [],
        otherData: [],
      };
    }
    acc[pmSpreadsheetId].clients.push(client);
    acc[pmSpreadsheetId].otherData.push(otherData);
    return acc;
  }, {} as Record<string, UniquePMEntry>);

  const defaultPM = incomingPMSpreadsheetId;
  const [selectedPM, setSelectedPM] = useState(defaultPM);
  const printPalletLabel = (
    palletNumber: string,
    includeCurrentItem: boolean = true
  ) => {
    if (!userAuthenticatedContext.token) {
      setInternalError("You must be logged in to print pallet labels.");
      return;
    }
    if (!selectedPM) {
      setInternalError("PM Spreadsheet ID is required to print pallet labels.");
      return;
    }
    if (processingPalletLabel) {
      setInternalError("A pallet label is already being processed.");
      return;
    }
    if (!selectedPalletType) {
      setInternalError("Invalid pallet type selected.");
      return;
    }

    setProcessingPalletLabel(true);
    setPalletRetrieveResults([]);
    setPalletLabelErrors([]);

    // If we are including the current item, we search a smaller area, otherwise we search a lot more of the sheet
    const offset = includeCurrentItem ? 1000 : 20000;

    sheetsWorker
      .retrievePalletLabelInformation(
        userAuthenticatedContext.token!,
        selectedPM,
        palletNumber,
        activePMID,
        selectedPalletType,
        includeCurrentItem ? currentRowPalletInfo : undefined,
        offset
      )
      .then((results) => {
        setPalletRetrieveResults(results.rowProcessingResults);
        setPalletLabelErrors(results.palletErrors || []);
        if (!results.isValid) {
          setShowPalletLabelErrorsModal(true);
        } else {
          // Use the new labelUrl if available, otherwise fall back to the old method
          const url =
            results.labelUrl ||
            `/pallet-label-generator?dataEncoded=${results.encodedInfo}`;
          window.open(url, "_blank");
        }
      })
      .finally(() => {
        setProcessingPalletLabel(false);
      });
  };

  const buttonWidth = "calc(50% - 2rem)";
  if (!userAuthenticatedContext?.token) {
    return <p>Please log in to print pallet labels.</p>;
  }
  return (
    <Container>
      {internalError ? <p>{internalError}</p> : null}
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span>Client Code</span>
            {defaultPM ? (
              <Button
                variant={darkMode ? "outline-light" : "outline-dark"}
                size="sm"
                onClick={() => {
                  if (incomingPMSpreadsheetId) {
                    setSelectedPM(defaultPM);
                  } else {
                    setInternalError("Please enter a valid PM Spreadsheet ID.");
                  }
                }}
                title={`Use Current PM (${
                  uniquePMs[defaultPM]?.otherData[0]?.clientNameTitle ||
                  "Unknown"
                })`}
              >
                Use Current PM
              </Button>
            ) : null}
          </div>
          <div className="mb-3">
            <Dropdown style={{ width: "100%" }}>
              <Dropdown.Toggle
                variant={!selectedPM ? "outline-danger" : "outline-success"}
                className="w-100 text-start"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  minHeight: "38px",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  padding: "8px 12px",
                }}
              >
                {selectedPM ? (
                  <div style={{ flex: 1, marginRight: "8px" }}>
                    <div
                      className="fw-bold"
                      style={{ wordBreak: "break-word" }}
                    >
                      {uniquePMs[selectedPM]?.otherData[0]?.clientNameTitle ??
                        uniquePMs[selectedPM]?.otherData[0]?.sfSupplierName ??
                        uniquePMs[selectedPM]?.pmSpreadsheetId}
                    </div>
                    <small
                      className="text-muted"
                      style={{ wordBreak: "break-word" }}
                    >
                      {uniquePMs[selectedPM]?.clients.join(", ") || "Unknown"}
                    </small>
                  </div>
                ) : (
                  "--- Select Client Code ---"
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu
                className="w-100"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                {Object.values(uniquePMs).map((pm) => (
                  <Dropdown.Item
                    key={pm.pmSpreadsheetId}
                    onClick={() => setSelectedPM(pm.pmSpreadsheetId)}
                    active={selectedPM === pm.pmSpreadsheetId}
                    style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ flex: 1, marginRight: "8px" }}>
                        <div className="fw-bold">
                          {pm.otherData[0]?.clientNameTitle ??
                            pm.otherData[0].sfSupplierName ??
                            pm.pmSpreadsheetId}
                        </div>
                        <small
                          className="text-muted"
                          style={{ wordBreak: "break-word" }}
                        >
                          {pm.clients.join(", ")}
                        </small>
                      </div>
                      {pm.pmSpreadsheetId === defaultPM && (
                        <Badge
                          bg="primary"
                          className="ms-2"
                          style={{ flexShrink: 0 }}
                        >
                          Current
                        </Badge>
                      )}
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <p className="mb-1">Pallet Number</p>
          <InputGroup className="mb-3">
            <FormControl
              type="text"
              value={palletNumber}
              isInvalid={!palletNumber}
              isValid={!!palletNumber}
              onChange={(e) => setPalletNumber(e.target.value)}
            />
            {palletNumberInput ? (
              <Button
                type="button"
                variant={darkMode ? "light" : "dark"}
                style={{ width: buttonWidth }}
                onClick={() => setPalletNumber(palletNumberInput)}
              >
                Use Current Row Pallet Number (
                <span>{palletNumberInput || "N/A"}</span>)
              </Button>
            ) : null}
          </InputGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <p className="mb-1">Stock Type</p>
          <InputGroup className="mb-3">
            <FormControl
              as="select"
              value={selectedPalletType}
              isInvalid={!selectedPalletType}
              isValid={!!selectedPalletType}
              onChange={(e) => {
                if (
                  e.target.value === "R" ||
                  e.target.value === "A" ||
                  e.target.value === "B"
                ) {
                  setSelectedPalletType(e.target.value as "R" | "A" | "B");
                } else {
                  setSelectedPalletType(undefined);
                }
              }}
            >
              <option value={undefined}>--- Select Pallet Type ---</option>
              <option value="R">Retail (R)</option>
              <option value="A">Auction (A)</option>
              <option value="B">BTS (B)</option>
            </FormControl>
            {palletTypeInput ? (
              <Button
                type="button"
                variant={darkMode ? "light" : "dark"}
                style={{ width: buttonWidth }}
                onClick={() => setSelectedPalletType(selectedPalletType)}
              >
                Use Current Row Pallet Type (
                <span>{palletTypeInput || "N/A"}</span>)
              </Button>
            ) : null}
          </InputGroup>
        </Col>
      </Row>
      <hr />
      <Row className="d-flex justify-content-between align-items-center">
        <Stack direction="horizontal" gap={2} className="w-100">
          {showPalletNumberCurrentRowComparison ? (
            <Col>
              {willPrintCurrentRow ? (
                <p className="text-success mb-0">
                  Pallet number includes current row:{" "}
                  {currentRowPalletInfo?.legacyPMID}
                </p>
              ) : (
                <p
                  className={
                    darkMode ? `text-warning mb-0` : `text-danger mb-0`
                  }
                >
                  Pallet number does not include{" "}
                  {currentRowPalletInfo?.legacyPMID}
                </p>
              )}
            </Col>
          ) : null}
          <div>
            {closePalletLabelPrintModal ? (
              <Button
                className="me-2"
                variant="warning"
                onClick={closePalletLabelPrintModal}
              >
                Close
              </Button>
            ) : null}
            <Button
              type="button"
              variant="primary"
              disabled={processingPalletLabel}
              onClick={() => {
                if (palletNumber) {
                  printPalletLabel(palletNumber, willPrintCurrentRow);
                } else {
                  setInternalError("Please enter a pallet number.");
                }
              }}
            >
              <Spinner
                animation="border"
                size="sm"
                role="status"
                className="me-2"
                hidden={!processingPalletLabel}
              />
              Print Pallet Label
              <Printer className="ms-2" size={20} />
            </Button>
          </div>
        </Stack>
      </Row>
      {/* Modal for pallet label errors */}
      <Modal
        show={showPalletLabelErrorsModal}
        onHide={() => setShowPalletLabelErrorsModal(false)}
        backdrop="static"
        keyboard={false}
        dialogClassName="modal-90w"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Pallet Label Errors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>There were errors found when generating the pallet label(s)</p>
          {palletLabelErrors.length > 0 ? (
            <ul>
              {palletLabelErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          ) : null}
          <Table>
            <thead>
              <tr>
                <th>PMID</th>
                <th>Listing Title</th>
                <th>Post Production Exit</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {palletRetrieveResults.map((row, index) => (
                <tr key={index}>
                  <td
                    style={{
                      backgroundColor: row.isValid
                        ? "var(--bs-success)"
                        : "var(--bs-danger)",
                      color: row.isValid ? "white" : "white",
                    }}
                  >
                    {row.pmid}
                  </td>
                  <td>{row.listingTitle}</td>
                  <td>{row.postProductionExit}</td>
                  <td
                    style={{
                      backgroundColor: row.isValid
                        ? "var(--bs-success)"
                        : "var(--bs-danger)",
                      color: row.isValid ? "black" : "white",
                    }}
                  >
                    {row.isValid ? "OK" : "Error"}
                  </td>
                  <td>{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPalletLabelErrorsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
